import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { Quotation } from '../data/dummyData';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface AnalyticsProps {
  quotations: Quotation[];
  onBackToDashboard: () => void;
  onNavigate: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const Analytics: React.FC<AnalyticsProps> = ({ quotations, onBackToDashboard, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'performance', label: 'Performance', icon: '📈' },
    { id: 'channels', label: 'Channels', icon: '📱' },
    { id: 'ai-insights', label: 'AI Insights', icon: '🤖' }
  ];

  // Calculate metrics
  const totalQuotations = quotations.length;
  const wonQuotations = quotations.filter(q => q.status === 'Won').length;
  const conversionRate = totalQuotations > 0 ? (wonQuotations / totalQuotations) * 100 : 0;

  // Channel distribution
  const channelData = quotations.reduce((acc, q) => {
    acc[q.channel] = (acc[q.channel] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const channelChartData = Object.entries(channelData).map(([channel, count]) => ({
    channel,
    count,
    percentage: ((count / totalQuotations) * 100).toFixed(1)
  }));

  // Service type distribution
  const serviceData = quotations.reduce((acc, q) => {
    acc[q.serviceType] = (acc[q.serviceType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const serviceChartData = Object.entries(serviceData).map(([service, count]) => ({
    service,
    count,
    percentage: ((count / totalQuotations) * 100).toFixed(1)
  }));

  // Monthly trend data
  const monthlyData = [
    { month: 'Jan', quotations: 45, won: 12 },
    { month: 'Feb', quotations: 52, won: 15 },
    { month: 'Mar', quotations: 48, won: 13 },
    { month: 'Apr', quotations: 61, won: 18 },
    { month: 'May', quotations: 55, won: 16 },
    { month: 'Jun', quotations: 67, won: 20 },
    { month: 'Jul', quotations: 72, won: 22 },
    { month: 'Aug', quotations: 68, won: 19 },
    { month: 'Sep', quotations: 74, won: 21 },
    { month: 'Oct', quotations: 82, won: 25 },
    { month: 'Nov', quotations: 78, won: 23 },
    { month: 'Dec', quotations: 85, won: 26 }
  ];

  // Priority distribution
  const priorityData = quotations.reduce((acc, q) => {
    acc[q.priority] = (acc[q.priority] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const priorityChartData = Object.entries(priorityData).map(([priority, count]) => ({
    priority,
    count,
    fill: priority === 'High' ? '#ef4444' : priority === 'Medium' ? '#f59e0b' : '#10b981'
  }));

  // AI insights data
  const aiInsights = {
    avgResponseTime: '2.3 hours',
    sentimentAnalysis: {
      positive: 68,
      neutral: 25,
      negative: 7
    },
    topKeywords: ['urgent', 'air-freight', 'exw', 'fob', 'customs'],
    recommendations: [
      'Increase focus on high-priority quotations',
      'Improve response time for WhatsApp inquiries',
      'Consider automated follow-ups for pending quotes'
    ]
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar currentView="analytics" onNavigate={onNavigate} />

      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Analytics & Insights</h1>
                <p className="text-gray-600 mt-1">Comprehensive analytics and AI-powered insights</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Last updated</p>
                  <p className="text-sm font-medium text-gray-900">Just now</p>
                </div>
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">📊</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="card mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Quotations</p>
                      <p className="text-2xl font-bold text-gray-900">{totalQuotations}</p>
                    </div>
                    <div className="text-2xl">📊</div>
                  </div>
                  <div className="mt-4 flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 ml-1">+12%</span>
                    <span className="text-sm text-gray-500 ml-2">vs last month</span>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                      <p className="text-2xl font-bold text-gray-900">{conversionRate.toFixed(1)}%</p>
                    </div>
                    <div className="text-2xl">🎯</div>
                  </div>
                  <div className="mt-4 flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 ml-1">+5.2%</span>
                    <span className="text-sm text-gray-500 ml-2">vs last month</span>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                      <p className="text-2xl font-bold text-gray-900">2.3h</p>
                    </div>
                    <div className="text-2xl">⏱️</div>
                  </div>
                  <div className="mt-4 flex items-center">
                    <TrendingDown className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 ml-1">-0.8h</span>
                    <span className="text-sm text-gray-500 ml-2">vs last month</span>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Value</p>
                      <p className="text-2xl font-bold text-gray-900">$185K</p>
                    </div>
                    <div className="text-2xl">💰</div>
                  </div>
                  <div className="mt-4 flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 ml-1">+18%</span>
                    <span className="text-sm text-gray-500 ml-2">vs last month</span>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Trend */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="quotations" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                      <Area type="monotone" dataKey="won" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Channel Distribution */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={channelChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ channel, percentage }) => `${channel} (${percentage}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {channelChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Win Rate by Service</h3>
                  <div className="space-y-3">
                    {serviceChartData.map((service, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{service.service}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary-600 h-2 rounded-full" 
                              style={{ width: `${service.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{service.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Distribution</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={priorityChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ priority, count }) => `${priority}: ${count}`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {priorityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Time Analysis</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Average Response Time</span>
                      <span className="text-lg font-semibold text-gray-900">2.3 hours</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Fastest Response</span>
                      <span className="text-lg font-semibold text-green-600">45 minutes</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Slowest Response</span>
                      <span className="text-lg font-semibold text-red-600">8.5 hours</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Target Response</span>
                      <span className="text-lg font-semibold text-blue-600">2 hours</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'channels' && (
            <div className="space-y-6">
              {/* Channel Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel Performance</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={channelChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="channel" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel Details</h3>
                  <div className="space-y-4">
                    {channelChartData.map((channel, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                          <span className="font-medium text-gray-900">{channel.channel}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900">{channel.count}</div>
                          <div className="text-sm text-gray-500">{channel.percentage}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-insights' && (
            <div className="space-y-6">
              {/* AI Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Analysis Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Average Response Time</span>
                      <span className="text-lg font-semibold text-gray-900">{aiInsights.avgResponseTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Sentiment Analysis</span>
                      <div className="flex space-x-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          {aiInsights.sentimentAnalysis.positive}% Positive
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {aiInsights.sentimentAnalysis.neutral}% Neutral
                        </span>
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                          {aiInsights.sentimentAnalysis.negative}% Negative
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🏷️ Top Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    {aiInsights.topKeywords.map((keyword, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        #{keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI Recommendations */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 AI Recommendations</h3>
                <div className="space-y-3">
                  {aiInsights.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                        {index + 1}
                      </div>
                      <p className="text-sm text-gray-700">{recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;